import { useState, useEffect, useMemo } from 'react';
import { ProfileCard } from '@components/assessors/atoms/ProfileCard/ProfileCard';
import { ProfileCardTitle } from '@components/assessors/atoms/ProfileCardTitle/ProfileCardTitle';
import { FeedbackForm } from '@components/assessors/molecules/FeedbackForm/FeedbackForm';
import { FeedbackDeleteModal } from '@components/assessors/molecules/FeedbackDeleteModal/FeedbackDeleteModal';
import { FeedbackEvaluation } from '@components/assessors/molecules/FeedbackEvaluation/FeedbackEvaluation';
import { Button } from '@acb_devel/design-system/Button';
import { CommentPlusIcon } from '@acb_devel/design-system/Icons';
import {
  getEvaluations,
  createEvaluation,
  updateEvaluation,
  deleteEvaluation,
} from '@services/evaluations';
import { ToastRoot } from '@acb_devel/design-system';
import { useToastContext } from '@acb_devel/design-system';
import { Alert } from '@acb_devel/design-system/Alert';
import { useUserContext } from '@contexts/UserContext';
import { useModal } from '@acb_devel/design-system/Modal';
import { Text } from '@acb_devel/design-system/Text';

import type { Registration } from '@services/registrations';
import type { User } from '@services/users';
import type {
  Evaluation,
  FeedbackForm as FeedbackFormParams,
} from '@services/evaluations';
import { RegistrationStatus } from '@services/registrations';

import styles from './FeedbackCard.module.scss';

export function FeedbackCard({
  user,
  registrations,
}: {
  user: User | undefined;
  registrations: Registration[];
}) {
  const { user: loggedUser } = useUserContext();
  const { isOpen, openModal, closeModal } = useModal();
  const { addToast } = useToastContext();

  const [addingFeedback, setAddingFeedback] = useState(false);
  const [eddingEvaluation, setEditingEvaluation] = useState(false);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [selectedEvaluation, setSelectedEvaluation] =
    useState<Evaluation | null>(null);

  function createFeedbackSubmit(data: FeedbackFormParams) {
    createEvaluation(data)
      .then((evaluation) => {
        setAddingFeedback(false);
        setEvaluations([evaluation, ...evaluations]);
        addToast(
          <ToastRoot>
            <Alert
              variant="success"
              direction="column"
              title={'Evaluación agregada correctamente'}
            />
          </ToastRoot>
        );
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al crear la evaluación`}
              text="Ha ocurrido un error al crear tu evaluación. Por favor, inténtalo de nuevo."
            />
          </ToastRoot>
        );

        console.error(error);
      });
  }

  function updateFeedbackSubmit(data: FeedbackFormParams) {
    if (!selectedEvaluation) {
      return;
    }

    updateEvaluation(selectedEvaluation.id, data)
      .then((evaluation) => {
        setSelectedEvaluation(evaluation);
        setEvaluations([evaluation, ...evaluations]);
        setEditingEvaluation(false);
        addToast(
          <ToastRoot>
            <Alert
              variant="success"
              direction="column"
              title={'Evaluación actualizada correctamente'}
            />
          </ToastRoot>
        );
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al actualizar la evaluación`}
              text="Ha ocurrido un error al actualizar tu evaluación. Por favor, inténtalo de nuevo."
            />
          </ToastRoot>
        );

        console.error(error);
      });
  }

  useEffect(() => {
    if (!user) {
      return;
    }

    getEvaluations({
      userId: user.id,
      ordering: '-created_at',
    }).then(({ results }) => {
      setEvaluations(results);
    });
  }, [user, setEvaluations, registrations]);

  useEffect(() => {
    setEditingEvaluation(false);
  }, [registrations]);

  const availableRegistrationsToReview = useMemo(
    () =>
      registrations.filter(
        (registration) =>
          registration.status === RegistrationStatus.CONFIRMED &&
          // check end event date is not older than 1 year
          new Date(registration.event.endEvent).getTime() >
            new Date(
              new Date().getTime() - 12 * 30 * 24 * 60 * 60 * 1000
            ).getTime() &&
          !evaluations.find(
            (evaluation) =>
              evaluation.registration.id === registration.id &&
              evaluation?.givenBy?.id === loggedUser?.id
          )
      ),
    [registrations, evaluations, loggedUser]
  );

  return (
    <>
      <ProfileCard className={styles.feedbackCard}>
        <ProfileCardTitle
          buttons={
            <>
              {!addingFeedback &&
                !eddingEvaluation &&
                availableRegistrationsToReview.length > 0 && (
                  <Button
                    variant="outline"
                    color="secondary"
                    size="md"
                    upperCase={false}
                    onClick={() => setAddingFeedback(true)}
                  >
                    <CommentPlusIcon /> Añadir Feedback
                  </Button>
                )}
            </>
          }
        >
          Feedback
        </ProfileCardTitle>
        {addingFeedback && !eddingEvaluation && (
          <FeedbackForm
            availableRegistrationsToReview={availableRegistrationsToReview}
            onSubmit={createFeedbackSubmit}
            onCancel={() => setAddingFeedback(false)}
          />
        )}
        {eddingEvaluation && selectedEvaluation?.registration.id && (
          <FeedbackForm
            availableRegistrationsToReview={availableRegistrationsToReview}
            defaultValues={{
              registrationId: selectedEvaluation.registration.id.toString(),
              name: selectedEvaluation?.name,
            }}
            onSubmit={updateFeedbackSubmit}
            onCancel={() => {
              setEvaluations([...evaluations, selectedEvaluation]);
              setEditingEvaluation(false);
            }}
          />
        )}
        {[...evaluations]
          .sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return b.createdAt.localeCompare(a.createdAt);
            }

            if (a.createdAt && !b.createdAt) {
              return -1;
            }

            if (!a.createdAt && b.createdAt) {
              return 1;
            }

            return -1; //null dates are last
          })
          .map((evaluation) => (
            <FeedbackEvaluation
              key={evaluation.id}
              evaluation={evaluation}
              onEdit={() => {
                setSelectedEvaluation(evaluation);
                setEvaluations(
                  evaluations.filter((ev) => ev.id !== evaluation.id)
                );
                setEditingEvaluation(true);
              }}
              onDelete={() => {
                setSelectedEvaluation(evaluation);
                openModal();
              }}
            />
          ))}
        {!evaluations.length && (
          <div className={styles.feedbackCard__empty}>
            <Text as="p" variant="body-2">
              No hay evaluaciones aún.
            </Text>
          </div>
        )}
      </ProfileCard>
      <FeedbackDeleteModal
        isOpen={isOpen}
        closeModal={closeModal}
        onDelete={() => {
          if (!selectedEvaluation) {
            return;
          }

          deleteEvaluation(selectedEvaluation?.id);
          setSelectedEvaluation(null);
          closeModal();
          setEvaluations(
            evaluations.filter(
              (evaluation) => evaluation.id !== selectedEvaluation?.id
            )
          );
          addToast(
            <ToastRoot>
              <Alert
                variant="success"
                direction="column"
                title={'Evaluación eliminada correctamente'}
              />
            </ToastRoot>
          );
        }}
      />
    </>
  );
}
